import { useEffect, useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * PayPal + Venmo checkout modal. Loads client config from /api/billing/config
 * on demand so no PayPal SDK is downloaded unless the user actually opts to upgrade.
 * Venmo appears automatically as a button when the merchant's PayPal account has it enabled
 * and the browser is on a supported (mobile) platform.
 *
 * Props:
 *   - playbookId: if set, the dialog switches to per-playbook unlock mode
 *     (calls /billing/playbook-orders, uses the playbook's price_usd, and
 *     only unlocks that one playbook rather than flipping has_premium).
 *   - priceOverride / productName: display overrides for per-playbook mode.
 *   - onUnlocked: extra callback after successful capture (per-playbook).
 */
export default function PayPalCheckoutDialog({ open, onOpenChange, playbookId = null, priceOverride = null, productName = null, onUnlocked = null }) {
  const { refreshUser } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isPerPlaybook = !!playbookId;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/billing/config");
        if (!cancelled) setConfig(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const canCheckout = useMemo(
    () => !!(config && config.enabled && config.client_id),
    [config]
  );

  const scriptOptions = useMemo(() => (
    canCheckout ? {
      clientId: config.client_id,
      currency: "USD",
      intent: "capture",
      "enable-funding": "venmo",
      "disable-funding": "credit,paylater",
    } : null
  ), [canCheckout, config]);

  const effectivePrice = isPerPlaybook
    ? (Number(priceOverride) || 0)
    : Number(config?.price_usd || 19);
  const priceLabel = config ? `$${effectivePrice.toFixed(2)}` : "";
  const displayProduct = isPerPlaybook
    ? (productName || "This playbook — one-time unlock")
    : (config?.product_name || "Premium Access");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none bg-[#0a0a0a] border border-slate-200" data-testid="paypal-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight text-xl text-slate-900 flex items-center gap-2">
            <span className="text-[#0D9488]">★</span> Unlock Premium
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="text-slate-600 text-sm font-mono-num">Loading checkout…</div>
        )}
        {error && (
          <div className="text-red-400 text-sm border border-red-900/60 bg-red-950/30 px-3 py-2 font-mono-num" data-testid="paypal-error">{error}</div>
        )}

        {config && !config.enabled && (
          <div className="text-sm text-slate-600 space-y-3">
            <p>Online payments haven't been enabled yet.</p>
            {config.venmo_handle ? (
              <div className="border border-[#0D9488] p-4 bg-[#1a1a1e]">
                <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Venmo P2P Fallback</div>
                <div className="text-slate-900 text-sm mb-2">Send <span className="text-[#0D9488] font-semibold">{priceLabel}</span> via Venmo to:</div>
                <a href={`https://venmo.com/${config.venmo_handle}`} target="_blank" rel="noopener noreferrer"
                   className="block font-mono-num text-lg text-[#0D9488] tracking-widest hover:underline break-all" data-testid="venmo-handle-link">
                  @{config.venmo_handle}
                </a>
                <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                  After paying, an admin will manually unlock your account within 24 hours.
                </p>
              </div>
            ) : (
              <p className="text-xs text-slate-500">Contact an admin to be manually unlocked.</p>
            )}
          </div>
        )}

        {canCheckout && (
          <>
            <div className="border border-slate-200 bg-white p-4">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">// One-time unlock</div>
              <div className="font-display font-black text-3xl text-slate-900 tracking-tight">{priceLabel}</div>
              <div className="text-xs text-slate-500 mt-1">{displayProduct}</div>
              {config.mode === "sandbox" && (
                <div className="mt-3 text-[10px] font-mono-num uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/5 px-2 py-1 inline-block" data-testid="paypal-sandbox-badge">
                  Sandbox mode — no real charge
                </div>
              )}
            </div>

            <PayPalScriptProvider options={scriptOptions}>
              <div data-testid="paypal-buttons-wrapper">
                <PayPalButtons
                  style={{ layout: "vertical", shape: "sharp", color: "gold", label: "paypal" }}
                  createOrder={async () => {
                    try {
                      const { data } = isPerPlaybook
                        ? await api.post("/billing/playbook-orders", { playbook_id: playbookId })
                        : await api.post("/billing/orders");
                      return data.id;
                    } catch (e) {
                      toast.error(formatApiError(e));
                      throw e;
                    }
                  }}
                  onApprove={async (data) => {
                    try {
                      if (isPerPlaybook) {
                        await api.post(`/billing/playbook-orders/${data.orderID}/capture`, { playbook_id: playbookId });
                        toast.success("Playbook unlocked!");
                        onUnlocked?.();
                      } else {
                        await api.post(`/billing/orders/${data.orderID}/capture`);
                        await refreshUser();
                        toast.success("Premium unlocked — welcome in!");
                      }
                      onOpenChange(false);
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  onError={(err) => {
                    toast.error("Payment failed — please try again.");
                    console.error("PayPal onError:", err);
                  }}
                />
              </div>
            </PayPalScriptProvider>

            {config.venmo_handle && (
              <div className="mt-2 text-xs text-slate-500 border-t border-slate-200 pt-3">
                Prefer Venmo P2P instead? Send {priceLabel} to
                <a href={`https://venmo.com/${config.venmo_handle}`} target="_blank" rel="noopener noreferrer"
                   className="text-[#0D9488] hover:underline ml-1" data-testid="venmo-p2p-fallback-link">
                  @{config.venmo_handle}
                </a>
                <span className="ml-1">— admin will unlock you manually.</span>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
